import {
  getCompaniesListing,
  setCompanyStatus as setCompanyStatusApi,
  putWhiteLabel as putWhiteLabelApi,
  removeCompanyWhiteLabel as removeCompanyWhiteLabelApi,
  putBranchWhiteLabel as putBranchWhiteLabelApi,
  removeBranchWhiteLabel as removeBranchWhiteLabelApi,
  getAccessRights as getAccessRightsApi,
  updateAccessRights as updateAccessRightsApi,
  getCompanyPlanInfo as getCompanyPlanInfoApi,
  updateCompanyPlan as updateCompanyPlanApi,
  getAvailablePlans as getAvailablePlansApi,
  getNodesWithWhiteLabel as getNodesWithWhiteLabelApi,
  getFeatureGroupOption as getFeatureGroupOptionApi,
  getCompanyFeature as getCompanyFeatureApi,
  setCompanyFeature as setCompanyFeatureApi,
  getFeatureStructure as getFeatureStructureApi,
  getCompanyExpiredDate as getCompanyExpiredDateApi,
  updateCompanyExpiredDate as updateCompanyExpiredDateApi,
  updateCompanySalesEmail as updateCompanySalesEmailApi,
  getCompanyUsers as getCompanyUsersApi,
  postCreditTransfer as postCreditTransferApi,
  postReimbursedCredit as postReimbursedCreditApi,
  getCompanyTags as getCompanyTagsApi,
  putCompanyTags as putCompanyTagsApi,
  getIndustries as getIndustriesApi,
  impersonate as impersonateApi,
  getProductList as getProductListApi,
  getBranchListByProduct as getBranchListByProductApi,
  getSnFormatsByBranch as getSnFormatsByBranchApi,
  checkSnAssignAvailability as checkSnAssignAvailabilityApi,
  updateAssign as updateAssignApi,
} from "app/api/companies.api";
import {
  setCompaniesData,
  setLoading,
  setError,
  setAccessRights,
  setAccessRightsError,
  resetAccessRights,
  setFeatureGroupOptions,
  setCompanyFeature as setCompanyFeatureAction,
  setFeatureStructure,
  setPlanInfo,
  setPlans,
  setNodesWithWhiteLabel,
  setCompanyUsers,
  setUsersLoading,
  setCompanyExpiredDate,
  setIndustries,
  setProductDropdown,
  setBranchDropdown,
  setSnFormatDropdown,
  setSnAvailabilityList,
  resetSNAvailabilityList as resetSNAvailabilityListAction,
  resetProductDropdown as resetProductDropdownAction,
  setAssignLoading,
} from "./companies.slice";

function processCompanyData(data) {
  return data.map((company, index) => {
    const processed = { company: {}, owner: {} };
    processed.company = {
      row: index,
      id: company.id || "",
      logo: company.image_logo_url || "",
      whiteLabel: company.white_label || "-",
      name: company.name || "",
      regNo: company.reg_no || "",
      industry: company.industry || "",
      email: company.email || "",
      contact: company.contact || "",
      addressLine1: company.line1 || "",
      addressLine2: company.contact || "",
      postcode: company.postal_code || "",
      state: company.state || "",
      city: company.city || "",
      country: company.country || "",
      status: company.status || "",
      feature_group: company.feature_group,
      plan: company.plan_name || "",
      expired_at: company.expired_at || "",
      deactive_at: company.deactive_at || "",
      sales_email: company.sales_email || "",
      api_access_key: company.api_access_key || null,
      api_access_secret: company.api_access_secret || null,
    };
    if (company.article_assoclation) {
      processed.company.articleOfAssoclationUrl = company.article_assoclation.url || "";
    }
    if (company.director_identification) {
      processed.company.directorIdentificationUrl = company.director_identification.url || "";
    }
    if (company.owner) {
      processed.owner = {
        firstName: company.owner.first_name || "",
        lastName: company.owner.last_name || "",
        contact: company.owner.phone_number || "",
        email: company.owner.email || "",
        timezone: company.owner.timezone || "",
      };
    }
    return { ...processed, branchWhiteLabelCount: company.branch_white_label_count || 0 };
  });
}

// --- Listing ---

// Only the newest listing request may write to the store. The Expires header stays clickable
// while a page loads, so asc then desc can both be in flight, and whichever lands last would
// otherwise win regardless of what the header now shows.
let latestListingRequest = 0;

export const fetchCompanies = (params = {}) => async (dispatch) => {
  const request = ++latestListingRequest;
  dispatch(setLoading(true));
  try {
    const response = await getCompaniesListing({
      start: (params.page || 0) * (params.length || 25),
      length: params.length || 25,
      companyName: params.companyName || "",
      regNo: params.regNo || "",
      plan: params.plan || "",
      status: params.status || "",
      // No expiredSort keeps the server's default order (newest company first).
      ...(params.expiredSort ? { column: "expired_at", order: params.expiredSort } : {}),
    });
    if (request !== latestListingRequest) return;
    const result = response.data.data || {};
    const processed = processCompanyData(result.data || []);
    dispatch(setCompaniesData({
      data: processed,
      totalRecords: result.recordsFiltered || 0,
      page: params.page || 0,
      branchWhiteLabelCounts: processed.map((c) => c.branchWhiteLabelCount),
    }));
  } catch (error) {
    if (request !== latestListingRequest) return;
    const status = error.response?.status;
    const msg = error.response?.data?.message;
    dispatch(setError(msg || (status ? `Failed to fetch companies (HTTP ${status})` : "Failed to fetch companies (network error)")));
  }
};

// --- Status ---

export const changeCompanyStatus = (id, status, remarks) => async () => {
  try {
    await setCompanyStatusApi(id, { status, remarks });
    return { success: true };
  } catch (error) {
    return { success: false, message: error.response?.data?.message || "Failed to update status" };
  }
};

// --- White Label ---

export const updateWhiteLabel = (id, whiteLabel) => async () => {
  try {
    await putWhiteLabelApi(id, { white_label: whiteLabel });
    return { success: true };
  } catch (error) {
    return { success: false, message: error.response?.data?.message || "Failed to update white label" };
  }
};

export const deleteCompanyWhiteLabel = (id) => async () => {
  try {
    await removeCompanyWhiteLabelApi(id);
    return { success: true };
  } catch (error) {
    return { success: false, message: error.response?.data?.message || "Failed to remove white label" };
  }
};

export const updateBranchWhiteLabel = (id, whiteLabel) => async () => {
  try {
    await putBranchWhiteLabelApi(id, { white_label: whiteLabel });
    return { success: true };
  } catch (error) {
    return { success: false, message: error.response?.data?.message || "Failed to update branch white label" };
  }
};

export const deleteBranchWhiteLabel = (id) => async () => {
  try {
    await removeBranchWhiteLabelApi(id);
    return { success: true };
  } catch (error) {
    return { success: false, message: error.response?.data?.message || "Failed to remove branch white label" };
  }
};

// --- Access Rights ---

export const fetchAccessRights = (uuid) => async (dispatch) => {
  dispatch(resetAccessRights());
  try {
    const response = await getAccessRightsApi(uuid);
    const payload = response.data.data || response.data || [];
    const ids = [];
    payload.forEach((group) => {
      (group.subgroup || []).forEach((s) => {
        (s.details || []).forEach((d) => {
          if (d.is_enabled) ids.push(d.id);
        });
      });
    });
    dispatch(setAccessRights({ list: payload, selectedIds: ids }));
  } catch (error) {
    dispatch(setAccessRightsError());
  }
};

export const saveAccessRights = (uuid, accessRights) => async () => {
  try {
    await updateAccessRightsApi(uuid, { access_rights: accessRights });
    return { success: true };
  } catch (error) {
    return { success: false, message: error.response?.data?.message || "Failed to update access rights" };
  }
};

// --- Plan ---

export const fetchCompanyPlanInfo = (id) => async (dispatch) => {
  dispatch(setPlanInfo(null));
  try {
    const response = await getCompanyPlanInfoApi(id);
    dispatch(setPlanInfo(response.data.data || response.data));
  } catch (error) {
    dispatch(setError(error.response?.data?.message || "Failed to fetch plan info"));
  }
};

export const fetchAvailablePlans = () => async (dispatch) => {
  try {
    const response = await getAvailablePlansApi();
    dispatch(setPlans(response.data.data || []));
  } catch (error) {
    dispatch(setError(error.response?.data?.message || "Failed to fetch plans"));
  }
};

export const swapCompanyPlan = (id, planUuid, needAccessRightUpdate) => async () => {
  try {
    await updateCompanyPlanApi(id, { plan_uuid: planUuid, need_access_right_update: needAccessRightUpdate });
    return { success: true };
  } catch (error) {
    return { success: false, message: error.response?.data?.message || "Failed to swap plan" };
  }
};

// --- Branch ---

export const fetchNodesWithWhiteLabel = (params) => async (dispatch) => {
  try {
    const length = params.length || 10;
    const page = params.page || 0;
    const response = await getNodesWithWhiteLabelApi(params.id, {
      start: page * length,
      length,
    });
    const payload = response.data?.data ?? response.data ?? {};
    dispatch(setNodesWithWhiteLabel({
      data: payload.data || [],
      total: payload.recordsTotal || payload.recordsFiltered || 0,
      page,
    }));
  } catch (error) {
    dispatch(setError(error.response?.data?.message || "Failed to fetch branches"));
  }
};

// --- Features ---

export const fetchFeatureGroupOptions = () => async (dispatch) => {
  try {
    const response = await getFeatureGroupOptionApi();
    dispatch(setFeatureGroupOptions(response.data.data || response.data || []));
  } catch (error) {
    dispatch(setError(error.response?.data?.message || "Failed to fetch feature groups"));
  }
};

export const fetchCompanyFeature = (id) => async (dispatch) => {
  dispatch(setCompanyFeatureAction(null));
  try {
    const response = await getCompanyFeatureApi(id);
    dispatch(setCompanyFeatureAction(response.data.data || response.data));
  } catch (error) {
    dispatch(setError(error.response?.data?.message || "Failed to fetch company feature"));
  }
};

export const saveCompanyFeature = (id, data) => async () => {
  try {
    await setCompanyFeatureApi(id, data);
    return { success: true };
  } catch (error) {
    return { success: false, message: error.response?.data?.message || "Failed to save company feature" };
  }
};

export const fetchFeatureStructure = () => async (dispatch) => {
  try {
    const response = await getFeatureStructureApi();
    dispatch(setFeatureStructure(response.data.data || response.data));
  } catch (error) {
    // Secondary fetch used by the feature dialog only — don't pollute the listing
    // error banner. The dialog can refetch if needed.
  }
};

// --- Expired Date ---

export const fetchCompanyExpiredDate = (id) => async (dispatch) => {
  try {
    const response = await getCompanyExpiredDateApi(id);
    dispatch(setCompanyExpiredDate(response.data.data || response.data));
  } catch (error) {
    dispatch(setError(error.response?.data?.message || "Failed to fetch expired date"));
  }
};

export const saveCompanyExpiredDate = (id, expiredAt) => async () => {
  try {
    await updateCompanyExpiredDateApi(id, { expired_at: expiredAt });
    return { success: true };
  } catch (error) {
    return { success: false, message: error.response?.data?.message || "Failed to update expired date" };
  }
};

// --- Sales Email ---

export const saveSalesEmail = (id, salesEmail) => async () => {
  try {
    await updateCompanySalesEmailApi(id, { sales_email: salesEmail });
    return { success: true };
  } catch (error) {
    return { success: false, message: error.response?.data?.message || "Failed to update sales email" };
  }
};

// --- Company Users ---

export const fetchCompanyUsers = (params) => async (dispatch) => {
  dispatch(setUsersLoading(true));
  try {
    const response = await getCompanyUsersApi(params);
    const result = response.data;
    dispatch(setCompanyUsers({
      data: result.data || [],
      total: result.recordsFiltered || result.recordsTotal || 0,
      page: params.page || 0,
    }));
  } catch (error) {
    dispatch(setUsersLoading(false));
  }
};

export const transferCredit = (data) => async () => {
  try {
    await postCreditTransferApi(data);
    return { success: true };
  } catch (error) {
    return { success: false, message: error.response?.data?.message || "Failed to transfer credit" };
  }
};

export const reimburseCredit = (data) => async () => {
  try {
    await postReimbursedCreditApi(data);
    return { success: true };
  } catch (error) {
    return { success: false, message: error.response?.data?.message || "Failed to reimburse credit" };
  }
};

// --- Industries (static reference data) ---

export const fetchIndustries = () => async (dispatch, getState) => {
  // Cache: only fetch once per session.
  if ((getState().companies.industries || []).length > 0) return;
  try {
    const response = await getIndustriesApi();
    const list = Array.isArray(response.data) ? response.data : (response.data?.data ?? []);
    dispatch(setIndustries(list));
  } catch (error) {
    // Non-fatal: industry name will fall back to its ID.
  }
};

// --- Tags ---

export const fetchCompanyTags = (uuid) => async () => {
  try {
    const response = await getCompanyTagsApi(uuid);
    return { success: true, data: response.data.data || response.data };
  } catch (error) {
    return { success: false, message: error.response?.data?.message || "Failed to fetch tags" };
  }
};

export const saveCompanyTags = (uuid, tags) => async () => {
  try {
    await putCompanyTagsApi(uuid, { tag: tags });
    return { success: true };
  } catch (error) {
    return { success: false, message: error.response?.data?.message || "Failed to update tags" };
  }
};

// --- Impersonate ---

export const impersonateUser = (uuid) => async () => {
  try {
    const response = await impersonateApi(uuid);
    return { success: true, data: response.data.data || response.data };
  } catch (error) {
    return { success: false, message: error.response?.data?.message || "Failed to impersonate" };
  }
};

// --- Serial Number Assign workflow ---

export const fetchProductsForCompany = (companyUuid) => async (dispatch) => {
  try {
    const response = await getProductListApi({ company_uuid: companyUuid });
    const list = response.data?.data || response.data || [];
    dispatch(setProductDropdown(list));
    return { success: true, data: list };
  } catch (error) {
    return { success: false, message: error.response?.data?.message || "Failed to fetch products" };
  }
};

export const fetchBranchesForProduct = (productUuid) => async (dispatch) => {
  try {
    const response = await getBranchListByProductApi(productUuid);
    const list = response.data?.data || response.data || [];
    dispatch(setBranchDropdown(list));
    return { success: true, data: list };
  } catch (error) {
    return { success: false, message: error.response?.data?.message || "Failed to fetch branches" };
  }
};

export const fetchSnFormatsForBranch = (branchUuid) => async (dispatch) => {
  try {
    const response = await getSnFormatsByBranchApi(branchUuid);
    const list = response.data?.data || response.data || [];
    dispatch(setSnFormatDropdown(list));
    return { success: true, data: list };
  } catch (error) {
    return { success: false, message: error.response?.data?.message || "Failed to fetch formats" };
  }
};

export const checkSnAvailability = (params) => async (dispatch) => {
  try {
    const response = await checkSnAssignAvailabilityApi({
      branch_uuid: params.branchUuid,
      format: params.format,
      ranges: params.ranges,
      is_sn: params.isSn,
    });
    const payload = response.data?.data || response.data || {};
    dispatch(setSnAvailabilityList({
      data: payload.list || payload.data || [],
      isSNexceedRange: payload.is_sn_exceed_range || payload.isSNexceedRange || false,
    }));
    return { success: true };
  } catch (error) {
    return { success: false, message: error.response?.data?.message || "Failed to check availability" };
  }
};

export const assignSerialNumbers = (payload) => async (dispatch) => {
  dispatch(setAssignLoading(true));
  try {
    const response = await updateAssignApi({
      branch_uuid: payload.branchUuid,
      product_uuid: payload.productUuid,
      format: payload.format,
      ranges: payload.ranges,
    });
    return { success: true, data: response.data?.data || response.data };
  } catch (error) {
    return { success: false, message: error.response?.data?.message || "Failed to assign serial numbers" };
  } finally {
    dispatch(setAssignLoading(false));
  }
};

export const resetSNAvailabilityList = () => (dispatch) => {
  dispatch(resetSNAvailabilityListAction());
};

export const resetProductDropdown = () => (dispatch) => {
  dispatch(resetProductDropdownAction());
};
